import * as React from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { type ExcludableFilterField } from '@ooh/contracts';
import { coerceNoPhoto, coerceExclude } from '../lib/sidecar-coercion';

/**
 * Numeric range filter params (CU-86cataqt9) now live in the neutral
 * `src/hooks/use-range-filter-params.ts` (CU-86ca9k73a extraction) so the
 * offer units-tab can share them without a cross-feature import
 * (WEB_ARCHITECTURE.md:36). Re-exported here for backward compatibility with
 * existing catalog callers (`use-media-units-page.ts`, `validate-search.ts`).
 */
export {
  readRangeFilterValues,
  buildNextRangeFilterParams,
  buildNextRangeFilterValues,
  useRangeFilterParams,
} from '../../../hooks/use-range-filter-params';

/**
 * URL `search` scalar param now lives in the neutral
 * `src/hooks/use-search-param.ts` (CU-86cbek5j5 extraction) so the map search
 * box can share it without a cross-feature import (WEB_ARCHITECTURE.md:36).
 * Re-exported here for backward compatibility with existing catalog callers
 * (`use-media-units-page.ts`).
 */
export {
  readSearchParam,
  buildNextSearchParam,
  useSearchParam,
} from '../../../hooks/use-search-param';

/**
 * Catalog "sidecar" filter params.
 * ===============================
 * These are the catalog-local filters that do NOT fit the array-only shared-filter model
 * (`useSharedFilters` / `SHARED_FILTER_FIELD_IDS`): the free-text `search` scalar, the
 * boolean `noPhoto` flag, and the per-field `exclude` mode array. They are modeled on the
 * near-POI sidecar (`usePoiFilterParams`): each hook reads from `useSearch` and writes back
 * via `navigate({ replace: true })`, preserving all other URL params.
 *
 * They are intentionally outside `SHARED_FILTER_FIELD_IDS`/`useSharedFilters` (the
 * catalog→map location/dimension carry-over model) — but `noPhoto` and the availability
 * date-range below are NOT catalog-only anymore: `MapPinsQuerySchema` carries both
 * dimensions too (map-catalog-filter-parity), so `/map` (`use-map-page-state.ts`) imports
 * `useNoPhotoParam`/`useAvailabilityRangeParams` directly from this module instead of
 * duplicating the URL-state logic. `search`, `exclude`, and `importJobId` remain
 * genuinely catalog-only: `search` is declared independently on each schema (identical
 * shape, not worth sharing), and `exclude`/`importJobId` have no map-pins equivalent at
 * all (see media-unit-filter-fields.ts).
 */

// Cast helper: navigate() without `from` has no concrete search type. Every setter
// below uses the FUNCTIONAL search updater (search reads LIVE state at commit time
// instead of a captured snapshot) so a setter that fires close to another
// filter-state setter never clobbers what the other just wrote (CU-86cataqt9 fix).
type AnyNavigate = (opts: {
  search: (prev: Record<string, unknown>) => Record<string, unknown>;
  replace: boolean;
}) => void;

// ─── noPhoto boolean ──────────────────────────────────────────

export function readNoPhotoParam(search: Record<string, unknown>): boolean {
  return coerceNoPhoto(search['noPhoto']);
}

export function buildNextNoPhotoParam(
  current: Record<string, unknown>,
  value: boolean,
): Record<string, unknown> {
  const next = { ...current };
  if (value) next['noPhoto'] = true;
  else delete next['noPhoto'];
  return next;
}

/** Reads the `noPhoto` boolean from the URL and returns a setter that writes it back. */
export function useNoPhotoParam(): [boolean, (value: boolean) => void] {
  const search = useSearch({ strict: false, structuralSharing: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const value = React.useMemo(() => readNoPhotoParam(search), [search]);
  const setValue = React.useCallback(
    (next: boolean) => {
      void (navigate as unknown as AnyNavigate)({
        search: (prev) => buildNextNoPhotoParam(prev, next),
        replace: true,
      });
    },
    [navigate],
  );
  return [value, setValue];
}

// ─── importJobId numeric scalar (catalog-only) ────────────────

export function readImportJobIdParam(search: Record<string, unknown>): number | undefined {
  const raw = search['importJobId'];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

export function buildNextImportJobIdParam(
  current: Record<string, unknown>,
  value: number | undefined,
): Record<string, unknown> {
  const next = { ...current };
  if (value != null) next['importJobId'] = value;
  else delete next['importJobId'];
  return next;
}

/** Reads the `importJobId` scalar from the URL and returns a setter that writes it back. */
export function useImportJobIdParam(): [number | undefined, (value: number | undefined) => void] {
  const search = useSearch({ strict: false, structuralSharing: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const value = React.useMemo(() => readImportJobIdParam(search), [search]);
  const setValue = React.useCallback(
    (next: number | undefined) => {
      void (navigate as unknown as AnyNavigate)({
        search: (prev) => buildNextImportJobIdParam(prev, next),
        replace: true,
      });
    },
    [navigate],
  );
  return [value, setValue];
}

// ─── exclude mode array ───────────────────────────────────────

export function readExcludeParam(search: Record<string, unknown>): ExcludableFilterField[] {
  return coerceExclude(search['exclude']);
}

export function buildNextExcludeParam(
  current: Record<string, unknown>,
  value: ExcludableFilterField[],
): Record<string, unknown> {
  const next = { ...current };
  if (value.length) next['exclude'] = value;
  else delete next['exclude'];
  return next;
}

// ─── availability date-range (two scalar bounds ↔ pill [from, to]) ──

/**
 * Reads the two scalar availability bounds into the DateRangePill's [from, to] array.
 * - both present → [from, to]
 * - one present  → [from, ''] / ['', to] (pill shows a partial selection)
 * - neither      → [] (pill inactive)
 */
export function readAvailabilityRangeParams(search: Record<string, unknown>): string[] {
  const from = typeof search['availabilityFrom'] === 'string' ? search['availabilityFrom'] : '';
  const to = typeof search['availabilityTo'] === 'string' ? search['availabilityTo'] : '';
  if (!from && !to) return [];
  return [from, to];
}

/**
 * Maps the pill's [from, to] array back to the two scalar URL params.
 * Both bounds present → set both. Otherwise (cleared, or single bound) → delete both,
 * because a single bound is ignored by the API and must not act as a filter.
 */
export function buildNextAvailabilityRangeParams(
  current: Record<string, unknown>,
  value: string[],
): Record<string, unknown> {
  const next = { ...current };
  const from = value[0] ?? '';
  const to = value[1] ?? '';
  if (from && to) {
    next['availabilityFrom'] = from;
    next['availabilityTo'] = to;
  } else {
    delete next['availabilityFrom'];
    delete next['availabilityTo'];
  }
  return next;
}

/**
 * Reads the availability range as the pill's [from, to] array and returns a setter
 * that writes both scalar params (or clears both). Mirrors usePoiFilterParams.
 */
export function useAvailabilityRangeParams(): [string[], (value: string[]) => void] {
  const search = useSearch({ strict: false, structuralSharing: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const value = React.useMemo(() => readAvailabilityRangeParams(search), [search]);
  const setValue = React.useCallback(
    (next: string[]) => {
      void (navigate as unknown as AnyNavigate)({
        search: (prev) => buildNextAvailabilityRangeParams(prev, next),
        replace: true,
      });
    },
    [navigate],
  );
  return [value, setValue];
}

/** Toggles a single field's membership in an exclude-mode list. */
export function toggleExcludeField(
  current: ExcludableFilterField[],
  fieldId: ExcludableFilterField,
): ExcludableFilterField[] {
  return current.includes(fieldId) ? current.filter((f) => f !== fieldId) : [...current, fieldId];
}

/**
 * Reads the `exclude` sidecar array from the URL and returns a per-field toggle.
 * Toggling one field's mode does not change another field's mode.
 */
export function useExcludeParam(): [
  ExcludableFilterField[],
  (fieldId: ExcludableFilterField) => void,
] {
  const search = useSearch({ strict: false, structuralSharing: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const value = React.useMemo(() => readExcludeParam(search), [search]);
  const toggle = React.useCallback(
    (fieldId: ExcludableFilterField) => {
      void (navigate as unknown as AnyNavigate)({
        search: (prev) => {
          const next = toggleExcludeField(readExcludeParam(prev), fieldId);
          return buildNextExcludeParam(prev, next);
        },
        replace: true,
      });
    },
    [navigate],
  );
  return [value, toggle];
}
